/**
 * GridBoard：4×4 十二宫网格（外圈 12 宫 + 中宫 2×2 CenterInfo）
 * 布局规范（速查表 28 节）：巳午未申/辰·中宫·酉/卯·中宫·戌/寅丑子亥
 */
import type { Base, RenshiMap, Zhi } from '../types/base';
import type { BirthInput } from '../types/api';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import CenterInfo from './CenterInfo';
import PalaceCell from './PalaceCell';
import { GRID_POS, ZHI_ORDER, innerEdgeCenter, sanfangSizheng, oppositeZhi } from './grid-layout';
import type { LayerKey, LayerPacks, LayerTab } from './layer-meta';

/** 十二地支方位（文墨天机图一：子=正北方 … 亥=北偏西） */
const DIRECTION: Record<Zhi, string> = {
  子: '正北方', 丑: '北偏东', 寅: '东偏北', 卯: '正东方',
  辰: '东偏南', 巳: '南偏东', 午: '正南方', 未: '南偏西',
  申: '西偏南', 酉: '正西方', 戌: '西偏北', 亥: '北偏西',
};
/** 盘外方位标注：左侧/右侧 top→bottom（row 0-3），上/下边（col 1-2） */
const LEFT_ZHIS: Zhi[] = ['巳', '辰', '卯', '寅'];
const RIGHT_ZHIS: Zhi[] = ['申', '酉', '戌', '亥'];
const TOP_ZHIS: Zhi[] = ['午', '未'];
const BOTTOM_ZHIS: Zhi[] = ['丑', '子'];



interface Props {
  base: Base;
  layer: LayerTab;
  packs: LayerPacks;
  /** 命例名称（中宫匿名显示） */
  name?: string;
  /** 命例录入原始输入（中宫钟表时间显示） */
  input?: BirthInput;
  /** 隐藏生辰（透传 CenterInfo：中宫匿名+时间/四柱置空） */
  hideBirth?: boolean;
  /** 点击宫位高亮（飞星四化）：当前点击的宫位地支 */
  activePalaceZhi?: Zhi | null;
  /** 三方四正虚线起点（点击宫为命宫；null=当前层命宫） */
  sanfangZhi?: Zhi | null;
  /** 点击宫位回调 */
  onClickPalace?: (zhi: Zhi) => void;
}

/** 解析点击宫的 fly（该宫干四化）→ 四化落点（星 + 落点宫地支）；离心=本宫，向心=对宫 */
export function parseFlyToLandings(
  fly: Record<string, string>,
  palaceZhi: Zhi,
): { kind: string; star: string; zhi: Zhi }[] {
  return (Object.entries(fly) as [string, string][]).map(([kind, raw]) => {
    const parts = raw.split(' ');
    const star = parts[0];
    if (raw.includes('离心自化')) return { kind, star, zhi: palaceZhi };
    if (raw.includes('向心自化')) return { kind, star, zhi: oppositeZhi(palaceZhi) };
    return { kind, star, zhi: parts[1] as Zhi };
  });
}

/**
 * 当前视角左下三组星映射：岁前/将前随层基准（本命=生年支、大限=大限命宫支、流年=流年支、
 * 流月=流月支、流日=流日支，速查表 25/26 节）；博士恒生年（速查表 16 节）
 */
function leftStarMaps(layer: LayerTab, base: Base, packs: LayerPacks): {
  suiqian?: RenshiMap;
  jiangqian?: RenshiMap;
  boshi?: RenshiMap;
} {
  if (layer === 'benming') {
    return { suiqian: base.suiqian, jiangqian: base.jiangqian, boshi: base.boshi };
  }
  const suiqian =
    layer === 'daxian' ? packs.daxian?.suiqian
      : layer === 'liunian' ? packs.liunian?.suiqian
        : layer === 'liuyue' ? packs.liuyue?.suiqian
          : packs.liuri?.suiqian;
  const jiangqian =
    layer === 'daxian' ? packs.daxian?.jiangqian
      : layer === 'liunian' ? packs.liunian?.jiangqian
        : layer === 'liuyue' ? packs.liuyue?.jiangqian
          : packs.liuri?.jiangqian;
  return { suiqian, jiangqian, boshi: base.boshi };
}

/**
 * 当前层三方四正（命财迁官）四宫地支：
 * 本命用 meta.ming；其他层从 renshigong 反查（值含"命"的键，比 zhi 更准确——
 * 如流月 zhi=节气月支≠命宫；大限/流年/流日/流时的 zhi=该层命宫支，但统一用 renshigong 查以防语义歧义）；
 * 流日层若已叠流时，以流时命宫为准（与 activeLayers 叠层顺序一致）
 */
function findMingZhi(rs: Record<string, string> | undefined): Zhi | undefined {
  if (!rs) return undefined;
  return (Object.entries(rs) as [Zhi, string][]).find(([, v]) => v.includes('命'))?.[0];
}

function currentSanfang(
  layer: LayerTab,
  base: Base,
  viewPacks: LayerPacks,
): { ming: Zhi; cai: Zhi; qian: Zhi; guan: Zhi } | null {
  let ming: Zhi | undefined;
  if (layer === 'benming') {
    ming = base.meta.ming;
  } else if (layer === 'daxian') {
    ming = findMingZhi(viewPacks.daxian?.renshigong) ?? viewPacks.daxian?.zhi;
  } else if (layer === 'liunian') {
    ming = findMingZhi(viewPacks.liunian?.renshigong) ?? viewPacks.liunian?.zhi;
  } else if (layer === 'liuyue') {
    ming = findMingZhi(viewPacks.liuyue?.renshigong) ?? viewPacks.liuyue?.zhi;
  } else if (layer === 'liuri') {
    ming = findMingZhi(viewPacks.liushi?.renshigong) ?? findMingZhi(viewPacks.liuri?.renshigong) ?? viewPacks.liuri?.zhi;
  }
  if (!ming) return null;
  return sanfangSizheng(ming);
}

/** 三方四正 4 条虚线（文墨天机：命-财、命-迁、命-官、财-官） */
const SANFANG_LINES: [keyof ReturnType<typeof sanfangSizheng>, keyof ReturnType<typeof sanfangSizheng>][] = [
  ['ming', 'cai'],
  ['ming', 'qian'],
  ['ming', 'guan'],
  ['cai', 'guan'],
];

/** 飞星四化虚线颜色（与 PalaceCell FLY_BG 同色系：禄绿/权紫/科蓝/忌红） */
const FLY_LINE_COLOR: Record<string, string> = {
  化禄: '#22c55e',
  化权: '#a855f7',
  化科: '#3b82f6',
  化忌: '#ef4444',
};

/** 飞星虚线段（像素坐标，相对宫格容器） */
interface FlyLine {
  x1: number; y1: number; x2: number; y2: number; color: string;
}

/** 立太极十二人事宫序（自太极命宫逆时针/地支递减）：命兄夫子财疾迁友官田福父 */
const TAIJI_ORDER = ['命', '兄', '夫', '子', '财', '疾', '迁', '友', '官', '田', '福', '父'];

/** 人事宫名 → 单字缩写（交友惯例用"友"，与 PalaceCell 层标签一致） */
function renshiShort(rn: string): string {
  return rn === '交友' ? '友' : rn.charAt(0);
}

export default function GridBoard({ base, layer, packs, name, input, hideBirth, activePalaceZhi, sanfangZhi, onClickPalace }: Props) {
  /** benming 层无叠加：以 daxian 键空跑 + 空 packs（清除缓存的层数据残留） */
  const layerKey: LayerKey = layer === 'benming' ? 'daxian' : layer;
  const viewPacks: LayerPacks = layer === 'benming' ? {} : packs;

  /** 地支 → 大限年龄段（左下显示；仅本命盘使用） */
  const ageByZhi = new Map(base.daxianSummary.map((d) => [d.zhi, d.ageRange]));

  /** 大限10年分布：大限盘（daxian）时各宫展示该大限年份+年龄 */
  const daxian10YearByZhi = useMemo(() => {
    if (layer !== 'daxian') return new Map<string, { year: number; age: number }>();
    const d = viewPacks.daxian;
    if (!d?.yearRange || !d?.ageRange) return new Map();
    const [startY, endY] = d.yearRange.split('-').map(Number);
    const [startA] = d.ageRange.split('-').map(Number);
    const m = new Map<string, { year: number; age: number }>();
    for (let i = 0; i <= endY - startY; i++) {
      const year = startY + i;
      const zhi = ZHI_ORDER[(year + 8) % 12];
      m.set(zhi, { year, age: startA + i });
    }
    return m;
  }, [layer, viewPacks.daxian]);

  /** 童限盘（速查表 31 节）：无大限年龄段，避免误挂第 1 大限区间 */
  const isTongxian = Boolean(viewPacks.daxian?.tongxian);

  /** 当前视角左下三组星映射（按层基准） */
  const maps = leftStarMaps(layer, base, viewPacks);

  // 点击宫位四化落点（activePalaceZhi 对应的 fly 解析）；无点击则 null
  const activeFly = useMemo(() => {
    if (!activePalaceZhi) return null;
    const p = base.benming.find((pp) => pp.zhi === activePalaceZhi);
    if (!p?.fly) return null;
    return parseFlyToLandings(p.fly, activePalaceZhi);
  }, [activePalaceZhi, base]);

  /** 飞星四化虚线：量取宫天干(data-fly-gan)与落点星名(data-fly-land)的 DOM 实际中心坐标，
   *  像素坐标画在无 viewBox 的 SVG 覆盖层上；ResizeObserver 应对转屏/字号变化重算 */
  const boardRef = useRef<HTMLDivElement>(null);
  const [flyLines, setFlyLines] = useState<FlyLine[]>([]);
  useLayoutEffect(() => {
    const board = boardRef.current;
    if (!board) return;
    const measure = () => {
      const rect = board.getBoundingClientRect();
      if (!activeFly || !activePalaceZhi) {
        setFlyLines([]);
        return;
      }
      const origin = board.querySelector<HTMLElement>(`[data-fly-gan="${activePalaceZhi}"]`);
      if (!origin) {
        setFlyLines([]);
        return;
      }
      const ox = origin.getBoundingClientRect();
      const lines: FlyLine[] = [];
      for (const f of activeFly) {
        const target = board.querySelector<HTMLElement>(`[data-fly-land="${f.zhi}:${f.kind}"]`);
        if (!target) continue;
        const tr = target.getBoundingClientRect();
        lines.push({
          x1: ox.left + ox.width / 2 - rect.left,
          y1: ox.top + ox.height / 2 - rect.top,
          x2: tr.left + tr.width / 2 - rect.left,
          y2: tr.top + tr.height / 2 - rect.top,
          color: FLY_LINE_COLOR[f.kind] ?? '#94a3b8',
        });
      }
      setFlyLines(lines);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(board);
    return () => ro.disconnect();
  }, [activeFly, activePalaceZhi, base]);

  /** 立太极：点击宫为新命宫，其余 11 宫标「前缀之后缀」（前缀=当前层点击宫人事宫缩写，
   *  后缀=相对太极的几何宫名）；仅本命/大限/流年三层生效，流月/流日/流时点击不显示；太极宫自身不标 */
  const taijiLabels = useMemo(() => {
    if (!activePalaceZhi) return undefined;
    let prefix = '';
    if (layer === 'benming') {
      const rn = base.benming.find((pp) => pp.zhi === activePalaceZhi)?.renshigong;
      if (rn) prefix = renshiShort(rn);
    } else if (layer === 'daxian' || layer === 'liunian') {
      const rn = (layer === 'daxian' ? viewPacks.daxian?.renshigong : viewPacks.liunian?.renshigong)?.[activePalaceZhi];
      if (rn) prefix = renshiShort(rn);
    } // 其余层（流月/日/时）return undefined
    if (!prefix) return undefined;
    const jIdx = ZHI_ORDER.indexOf(activePalaceZhi);
    const out: Partial<Record<Zhi, string>> = {};
    for (const p of base.benming) {
      if (p.zhi === activePalaceZhi) continue;
      const tIdx = ZHI_ORDER.indexOf(p.zhi);
      out[p.zhi] = `${prefix}之${TAIJI_ORDER[(jIdx - tIdx + 12) % 12]}`;
    }
    return out;
  }, [activePalaceZhi, layer, viewPacks.daxian, viewPacks.liunian, base]);

  /** 12 外圈宫按 GRID_POS 显式定位（不依赖 auto-placement，中宫跨格不乱序） */
  const outer = base.benming.map((palace) => {
    const { row, col } = GRID_POS[palace.zhi];
    return (
      <div key={`p${palace.zhi}`} className="min-h-0" style={{ gridColumn: col + 1, gridRow: row + 1 }}>
        <PalaceCell
          palace={palace}
          layer={layerKey}
          packs={viewPacks}
          shenZhi={base.meta.shen}
          daxianAgeRange={layer === 'benming' && !isTongxian ? ageByZhi.get(palace.zhi) : undefined}
          daxianYear={daxian10YearByZhi.get(palace.zhi)}
          isBenming={layer === 'benming'}
          liunianByZhi={base.liunianByZhi}
          xiaoxianByZhi={base.xiaoxianByZhi}
          activeFly={activeFly}
          taijiLabels={taijiLabels}
          onClickPalace={onClickPalace}
          leftStars={{
            suiqian: maps.suiqian?.[palace.zhi],
            jiangqian: maps.jiangqian?.[palace.zhi],
            boshi: maps.boshi?.[palace.zhi],
          }}
        />
      </div>
    );
  });

  /** 当前层三方四正四宫（null=层数据未就绪，不画线） */
  /** 三方四正虚线：点击宫为命宫时从点击宫出发；否则当前层命宫 */
  const sanfang = sanfangZhi ? sanfangSizheng(sanfangZhi) : currentSanfang(layer, base, viewPacks);

  return (
    <div className="flex min-h-0 w-full max-w-[40rem] flex-1 flex-col pt-1 pb-2 max-sm:-mx-4 max-sm:min-w-[calc(100%+2rem)] sm:mx-auto">
      <div className="flex min-h-0 flex-1 items-stretch justify-center">
        {/* 宫格容器：flex 列 + 子项 flex-1 撑满高度（不设显式高度，靠父级 items-stretch 拉伸；
         *  百分比高度链在 flex 拉伸中不可靠，统一用 flex 链） */}
        <div ref={boardRef} className="relative flex flex-col" style={{ width: 'calc(4 * var(--cell, 7.5rem) + 3px)' }}>
            {/* 三方四正虚线（文墨天机：命财迁官连线，随当前层命宫变化；viewBox 1 格=100 与 7.5rem 宫格等比例） */}
            {sanfang && (
              <svg
                className="pointer-events-none absolute inset-0 z-10 h-full w-full"
                viewBox="0 0 400 400"
                preserveAspectRatio="none"
              >
                {SANFANG_LINES.map(([a, b], i) => {
                  // 端点 = 各宫面向盘心的内边线中心点（两点都在中宫边界上，线段天然不出中宫）
                  const ea = innerEdgeCenter(GRID_POS[sanfang[a]].row, GRID_POS[sanfang[a]].col);
                  const eb = innerEdgeCenter(GRID_POS[sanfang[b]].row, GRID_POS[sanfang[b]].col);
                  return (
                    <line
                      key={i}
                      x1={ea.x}
                      y1={ea.y}
                      x2={eb.x}
                      y2={eb.y}
                      stroke="#94a3b8"
                      strokeWidth="0.75"
                      strokeDasharray="2.5 2"
                      opacity="0.7"
                    />
                  );
                })}
              </svg>
            )}
            {/* 飞星四化虚线（点击宫天干 → 四化落点星名，禄绿/权紫/科蓝/忌红，无箭头）；
                无 viewBox，坐标即量取的 CSS 像素值 */}
            {flyLines.length > 0 && (
              <svg className="pointer-events-none absolute inset-0 z-10 h-full w-full">
                {flyLines.map((l, i) => (
                  <line
                    key={i}
                    x1={l.x1}
                    y1={l.y1}
                    x2={l.x2}
                    y2={l.y2}
                    stroke={l.color}
                    strokeWidth="1.5"
                    strokeDasharray="5 4"
                    opacity="0.9"
                  />
                ))}
              </svg>
            )}
            {/* 十二地支方位标注：文字位于宫格边线外侧、紧贴边线（窄屏 8px 小字，把横向空间让给宫格） */}
            <div className="pointer-events-none absolute inset-0 z-10 text-[8px] text-slate-400 sm:text-[10px]">
              {LEFT_ZHIS.map((z, i) => (
                <span
                  key={z}
                  className="absolute [writing-mode:vertical-rl]"
                  style={{ left: '-0.75rem', top: `calc(${(i + 0.5) / 4} * 100%)`, transform: 'translateY(-50%)' }}
                >
                  {DIRECTION[z]}
                </span>
              ))}
              {RIGHT_ZHIS.map((z, i) => (
                <span
                  key={z}
                  className="absolute [writing-mode:vertical-rl]"
                  style={{ right: '-0.75rem', top: `calc(${(i + 0.5) / 4} * 100%)`, transform: 'translateY(-50%)' }}
                >
                  {DIRECTION[z]}
                </span>
              ))}
              {TOP_ZHIS.map((z, i) => (
                <span
                  key={z}
                  className="absolute"
                  style={{ top: '-0.6rem', left: `calc(${(i + 1.5) / 4} * 100%)`, transform: 'translateX(-50%)' }}
                >
                  {DIRECTION[z]}
                </span>
              ))}
              {BOTTOM_ZHIS.map((z, i) => (
                <span
                  key={z}
                  className="absolute"
                  style={{ bottom: '-0.7rem', left: `calc(${(i + 1.5) / 4} * 100%)`, transform: 'translateX(-50%)' }}
                >
                  {DIRECTION[z]}
                </span>
              ))}
            </div>

            <div
              className="grid min-h-0 flex-1 grid-cols-4 gap-px bg-slate-800"
              style={{
                gridTemplateColumns: 'repeat(4, var(--cell, 7.5rem))',
                // 行高下限 6.75rem（108px）：保证极端情况（6 个四化同宫）也显示完整；
                // 可用空间更大时 1fr 继续拉伸
                gridTemplateRows: 'repeat(4, minmax(6.75rem, 1fr))',
              }}
            >
              {outer}
              {/* 中宫 2×2 跨格信息区 */}
              <div style={{ gridColumn: '2 / 4', gridRow: '2 / 4' }}>
                <CenterInfo meta={base.meta} name={name} input={input} hideBirth={hideBirth} />
              </div>
            </div>
          </div>
        </div>
    </div>
  );
}
